import { defineStore } from 'pinia';
import { UserMenu } from '@/apis/types/userMgr';
import {
  getMenuKey,
  kolLoginBycaptcha,
  loginApi,
  logoutApi,
  onlineLogin,
  adLoginApi
} from '@/apis/login/index';
import { getUserDetail } from '@/apis/userMgr';
import { MessagePlugin } from 'tdesign-vue-next';
import { permissionApi } from '@/apis/auth';
import { useI18n } from '@/hooks/web/useI18n';
import { checkProcess } from '@/apis/buildingElevator/residentialBuilding';
import { Menu } from '@/router/types';
import { filterRoutesFun, specialMenuOnline } from '@/layouts/default/menu/menuFilter';
import { getMicroAppInstance } from '@/microApp';
import { persistEncryption } from '../plugin/persist';
import { useKOLInfoStore } from './koneOnlineInfo';
import { setRumUserLogout, setRumUserContext, initRum } from '@/datafluxRum';

const { t } = useI18n();

export type SwitchEnumFlag = 'local' | 'remote';

export const useUserInfoStore = defineStore('userInfo', {
  state: () => ({
    token: '',
    expiredTime: 10000,
    userId: '' as number | string,
    userName: '',
    nickName: '',
    remoteToken: '',
    isRemoteUser: false,
    isRemoteAdmin: false,
    phoneCallElevatorGranted: false, //是否有手机呼梯（手机用户管理）权限
    currentMenus: [] as Menu[],
    rememberMe: false,
    rememberInfo: {} as { account: string; password: string },
    rememberLoginType: 'remote' as SwitchEnumFlag,
    roles: [] as string[]
  }),
  actions: {
    async login(params: any) {
      const { code, msg, data } = await loginApi(params);
      if (code === '200') {
        this.userName = params.username;
        this.token = data.accessToken;
        // setLocalToken(data.accessToken)
        this.expiredTime = data.expiredTime;
        this.userId = data.userId;
        this.isRemoteUser = false;
        return data;
      } else {
        MessagePlugin.error(msg);
        return;
      }
    },
    async kolLogin(params: { phoneNumber: string; code: string }) {
      const res = await kolLoginBycaptcha({
        ...params,
        userType: 2
      });
      if (res && res.code && Number(res.code) === 200) {
        initRum();
        const { accessToken, userId } = res.data;
        this.userId = userId;
        const onlineLoginSuccess = await this.onlineLogin({ onlineToken: accessToken });
        return onlineLoginSuccess;
      } else {
        MessagePlugin.error(res.msg ?? t('login.loginFailed'));
        return;
      }
    },
    async onlineLogin(params: { onlineToken: string }) {
      const { code, msg, data } = await onlineLogin(params);
      if (code === '200') {
        setRumUserContext(data);
        this.userName = data.username;
        this.token = data.accessToken;
        // setLocalToken(data.accessToken)
        this.expiredTime = data.expiredTime;
        this.userId = data.userId;
        this.remoteToken = params.onlineToken;
        // setLocalToken(params.onlineToken, true)
        this.isRemoteUser = true;
        this.checkBuildingElevatorProcess();
        // const res = await checkMasterAccount()
        // if (res && res.code && Number(res.code) === 200) {
        //   this.isRemoteAdmin = res.data
        // }
        this.isRemoteAdmin = data?.['masterAccountFlag'] == true;
        return data;
      } else {
        MessagePlugin.error(msg);
        return;
      }
    },
    async adLogin(tokenId: string) {
      const { code, msg, data } = await adLoginApi({ tokenId });
      if (code === '200') {
        this.userName = data.username;
        this.token = data.accessToken;
        this.expiredTime = data.expiredTime;
        this.userId = data.userId;
        return true;
      } else {
        MessagePlugin.error(msg);
        return false;
      }
    },
    async logout() {
      if (this.token) {
        await logoutApi();
      }
      this.logoutStatus();
      console.log('======userLogout======');
    },
    logoutStatus(needCheck?: boolean) {
      if (needCheck === true && !this.token) {
        return;
      }
      if (import.meta.env.MODE == 'web') {
        sessionStorage.removeItem('isLoginSuccess');
      }
      localStorage.removeItem('userMenus');
      useKOLInfoStore().logout();
      if (this.isRemoteUser) {
        getMicroAppInstance().unmountAllApps({
          destroy: false,
          clearAliveState: true,
          clearData: true
        });
        setRumUserLogout();
      }
      this.$patch({
        token: '',
        userId: '',
        userName: '',
        nickName: '',
        isRemoteUser: false,
        remoteToken: '',
        isRemoteAdmin: false,
        phoneCallElevatorGranted: false
      });

      // 删除子窗口
      window.electron?.ipcRenderer.send('delete-child-window');

      if (!this.rememberMe) {
        this.rememberInfo = { account: '', password: '' };
      }
    },
    async updateUserMenu() {
      let result: UserMenu[] | null = null;
      if (this.isRemoteUser) {
        const res = await permissionApi({});

        const menuData: UserMenu[] = [];

        if (res && res.permissions.length > 0) {
          // 预处理KoneOnline用户信息数据
          useKOLInfoStore().init(res.permissions);
          res.permissions.forEach((parent) => {
            menuData.push({ menuName: parent.permissionName, menuKey: parent.permissionCode });
            if (parent.children && parent.children.length > 0) {
              menuData.push(
                ...parent.children.map((item) => {
                  return { menuName: item.permissionName, menuKey: item.permissionCode };
                })
              );
            }
          });
          result = specialMenuOnline(this.isRemoteAdmin, menuData);
          setLocalUserMenus(result);
        } else {
          if (res && res.permissions && res.permissions.length == 0) {
            try {
              this.logout();
            } catch (e) {
              console.log(e);
            }
          }
          MessagePlugin.error(t('login.tipKOLApiFailed'));
        }
      } else {
        const res = await getMenuKey();
        if (Number(res.code) === 200) {
          result = res.data.filter(
            (menu) => menu && menu?.menuKey !== undefined && menu?.menuKey !== ''
          );
          setLocalUserMenus(result);
          console.log('======userMenu======\n', result);
        }
      }
      // 按接口返回菜单数据过滤、排序、重命名本地菜单
      this.currentMenus = filterRoutesFun({
        userMenus: result ?? [],
        isRemoteUser: this.isRemoteUser
      });

      return this.currentMenus;
    },
    async updateUserInfo() {
      if (this.userId) {
        const res = await getUserDetail(this.userId);
        const { data } = res;
        this.nickName = data.nickName;
        this.userName = data.userName;
      }
    },
    isAdminByEnv() {
      if (this.isRemoteUser) {
        return this.isRemoteAdmin;
      } else {
        return this.userId === 1;
      }
    },
    checkBuildingElevatorProcess() {
      return new Promise((resolve) => {
        checkProcess()
          .then((res) => {
            const { code, data } = res;
            //有手机呼梯管理权限 则户室必填
            if (code && code == '200') {
              this.phoneCallElevatorGranted = data === true;
            } else {
              this.phoneCallElevatorGranted = false;
            }
            resolve(res);
          })
          .catch((e) => {
            console.log(e);
            resolve(false);
          });
      });
    },
    setRemoteToken(token: string) {
      this.remoteToken = token;
    },
    updateRoles(roles: string[]) {
      this.roles = roles;
    }
  },
  persist: [
    {
      storage: localStorage,
      pick: [
        'token',
        'remoteToken',
        'userId',
        'userName',
        'nickName',
        'isRemoteUser',
        'isRemoteAdmin',
        'phoneCallElevatorGranted',
        'rememberMe',
        'rememberInfo',
        'rememberLoginType'
      ]
    }
  ]
});

export function getLocalUserMenus(): UserMenu[] {
  return JSON.parse(persistEncryption.decrypt(localStorage.getItem('userMenus') ?? '') || '[]');
}

export function setLocalUserMenus(data: UserMenu[]) {
  localStorage.setItem('userMenus', persistEncryption.encrypt(JSON.stringify(data)));
}
